import { max, min } from 'd3-array';
import { ScaleBand, ScaleLinear, scaleLinear, ScaleTime, scaleTime } from 'd3-scale';
import { get, head, isNumber, noop, sortBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';

import { Data, Dimensions, formatDate, formatMessage, GraphType } from './types';

const HORIZONTAL_OFFSET = 70;
const RIGHT_HORIZONTAL_OFFSET = 70;

export type GraphContextType = {
    data: Data;
    graphType: GraphType;
    xScale: ScaleTime<number, number> | ScaleBand<number>;
    leftYScale: ScaleLinear<number, number>;
    rightYScale?: ScaleLinear<number, number>;
    formatDate: formatDate;
    formatMessage: formatMessage;
    dimensions: Dimensions;
    yZeroPosition: number;
    hoverInfoXOffset?: number;
    setHoverInfoXOffset?: (hoverInfoXOffset: number) => void;
};

export const GraphContext = React.createContext<GraphContextType>({
    data: [],
    graphType: GraphType.SINGLE_UP_BAR,
    xScale: scaleTime<number, number>(),
    leftYScale: scaleLinear(),
    rightYScale: scaleLinear(),
    formatDate: () => '',
    formatMessage: () => '',
    dimensions: {
        height: 0,
        width: 0,
        margin: 0,
        horizontalMargin: undefined,
        widthWithoutMargin: 0,
        heightWithoutMargin: 0,
        legendHeight: 0,
        legendLeftMargin: 0,
        legendTopMargin: 0,
    },
    yZeroPosition: 0,
    hoverInfoXOffset: 0,
    setHoverInfoXOffset: () => {},
});

type props = {
    children?: React.ReactNode;
    data: Data;
    dimensions: {
        height: number;
        width: number;
        margin: number;
        horizontalMargin?: number;
        legendHeight?: number;
        legendLeftMargin?: number;
        legendTopMargin?: number;
    };
    formatMessage: ({ id }: { id: string }) => string;
    formatDate?: (date: Date) => string;
    xScale?: ScaleTime<number, number> | ScaleBand<number>;
    leftYScale?: ScaleLinear<number, number>;
    rightYScale?: ScaleLinear<number, number>;
    hoverInfoXOffset?: number;
    setHoverInfoXOffset?: (hoverInfoXOffset: number) => void;
    setYZeroPosition?: (yZeroPosition: number) => void;
    yScaleShouldStartAtZero?: boolean;
    graphType?: GraphType;
    shouldRoundYScales?: boolean;
    leftYScaleUnit?: string;
    rightYScaleUnit?: string;
};

const GraphDataProvider: React.FC<props> = ({
    data,
    dimensions: {
        height,
        width,
        margin,
        horizontalMargin,
        legendLeftMargin = 0,
        legendTopMargin = 0,
        legendHeight = 0,
    },
    formatMessage = () => '',
    formatDate = () => '',
    children,
    xScale,
    leftYScale,
    rightYScale,
    hoverInfoXOffset = 0,
    setHoverInfoXOffset = noop,
    setYZeroPosition = noop,
    yScaleShouldStartAtZero = false,
    graphType = GraphType.SINGLE_UP_BAR,
    shouldRoundYScales = false,
    leftYScaleUnit,
    rightYScaleUnit,
}) => {
    const largestXData = get(head(sortBy(data, 'x').reverse()), 'x', new Date());
    const smallestXData = get(head(sortBy(data, 'x')), 'x', new Date());
    const xDomain = [moment(smallestXData).toDate(), moment(largestXData).toDate()];
    xScale =
        xScale ||
        scaleTime()
            .domain(xDomain)
            .range([
                (horizontalMargin || margin) + HORIZONTAL_OFFSET,
                width - (horizontalMargin || margin) - RIGHT_HORIZONTAL_OFFSET,
            ]);

    let leftYData: number[] | undefined = undefined,
        leftLargestYDataPoint = 0,
        leftSmallestYDataPointMinus10Percent = 0,
        rightYData: number[] | undefined = undefined,
        rightLargestYDataPoint = 0,
        rightSmallestYDataPointMinus10Percent = 0,
        yZeroPosition: number | undefined = undefined;

    switch (graphType) {
        case GraphType.SINGLE_UP_BAR: {
            leftYData = data.map((d) => d.leftUpY).filter(isNumber);
            leftLargestYDataPoint = max<number>(leftYData) || 0;
            const leftSmallestYDataPoint = min<number>(leftYData) || 0;
            leftSmallestYDataPointMinus10Percent = Math.max(
                leftSmallestYDataPoint - (leftLargestYDataPoint - leftSmallestYDataPoint) * 0.1,
                0
            );
            yZeroPosition = height - margin;
            break;
        }
        case GraphType.TWO_UP_BARS: {
            leftYData = data.map((d) => d.leftUpY).filter(isNumber);
            leftLargestYDataPoint = max<number>(leftYData) || 0;
            const leftSmallestYDataPoint = min<number>(leftYData) || 0;
            leftSmallestYDataPointMinus10Percent = Math.max(
                leftSmallestYDataPoint - (leftLargestYDataPoint - leftSmallestYDataPoint) * 0.1,
                0
            );

            rightYData = data.map((d) => d.rightUpY).filter(isNumber);
            rightLargestYDataPoint = max<number>(rightYData) || 0;
            const rightSmallestYDataPoint = min<number>(rightYData) || 0;
            rightSmallestYDataPointMinus10Percent = Math.max(
                rightSmallestYDataPoint - (rightLargestYDataPoint - rightSmallestYDataPoint) * 0.1,
                0
            );

            yZeroPosition = height - margin;
            break;
        }
        case GraphType.ONE_UP_AND_ONE_DOWN_BARS: {
            const leftUpYData = data.map((d) => d.leftUpY).filter(isNumber);
            const leftDownYData = data
                .map((d) => (d.leftDownY != null ? -Math.abs(d.leftDownY) : null))
                .filter(isNumber);
            leftYData = leftUpYData.concat(leftDownYData);
            leftLargestYDataPoint = max<number>(leftYData) || 0;
            const leftSmallestDataPoint = min<number>(leftYData) || 0;
            leftSmallestYDataPointMinus10Percent =
                leftSmallestDataPoint > 0
                    ? Math.max(leftSmallestDataPoint - (leftLargestYDataPoint - leftSmallestDataPoint) * 0.1, 0)
                    : Math.min(leftSmallestDataPoint - (leftLargestYDataPoint - leftSmallestDataPoint) * 0.1, 0);
            break;
        }
        case GraphType.TWO_UP_AND_ONE_DOWN_BARS: {
            leftYData = data.map((d) => d.leftUpY).filter(isNumber);
            leftLargestYDataPoint = max<number>(leftYData) || 0;
            const leftSmallestYDataPoint = min<number>(leftYData) || 0;
            leftSmallestYDataPointMinus10Percent = Math.max(
                leftSmallestYDataPoint - (leftLargestYDataPoint - leftSmallestYDataPoint) * 0.1,
                0
            );

            const rightUpYData = data.map((d) => d.rightUpY).filter(isNumber);
            rightLargestYDataPoint = max<number>(rightUpYData) || 0;

            const rightDownYData = data
                .map((d) => {
                    if (!isNumber(d.rightDownY)) return null;
                    return -Math.abs(d.rightDownY);
                })
                .filter(isNumber);

            rightYData = rightUpYData.concat(rightDownYData);
            const rightSmallestDataPoint = min<number>(rightYData) || 0;
            rightSmallestYDataPointMinus10Percent = rightSmallestDataPoint;

            break;
        }
    }

    const shouldUseUnifiedScale = leftYScaleUnit === rightYScaleUnit && leftYScaleUnit && rightYScaleUnit;
    const unifiedMaxValue = Math.max(leftLargestYDataPoint, rightLargestYDataPoint);

    let rightYDomain = useMemo(() => {
        if (graphType === GraphType.TWO_UP_BARS) {
            if (shouldUseUnifiedScale && yScaleShouldStartAtZero) {
                return [unifiedMaxValue, 0];
            }
            return yScaleShouldStartAtZero
                ? [rightLargestYDataPoint, 0]
                : [rightLargestYDataPoint, rightSmallestYDataPointMinus10Percent];
        } else if (graphType === GraphType.TWO_UP_AND_ONE_DOWN_BARS) {
            return [rightLargestYDataPoint, rightSmallestYDataPointMinus10Percent];
        }

        return [0, 0];
    }, [
        graphType,
        rightLargestYDataPoint,
        rightSmallestYDataPointMinus10Percent,
        leftLargestYDataPoint,
        yScaleShouldStartAtZero,
        shouldUseUnifiedScale,
        unifiedMaxValue,
    ]);

    rightYScale = useMemo(() => {
        if (graphType === GraphType.TWO_UP_AND_ONE_DOWN_BARS && rightSmallestYDataPointMinus10Percent < 0) {
            const recuperationRange = Math.abs(rightSmallestYDataPointMinus10Percent);
            const positiveRange = rightLargestYDataPoint;
            const recuperationRatio = recuperationRange / positiveRange;

            if (recuperationRatio < 0.3) {
                const scale =
                    rightYScale ||
                    scaleLinear()
                        .domain([rightLargestYDataPoint, rightSmallestYDataPointMinus10Percent])
                        .range([margin, height - margin]);
                const niceScale = shouldRoundYScales ? scale.nice() : scale;
                return niceScale;
            } else {
                const maxAbsValue = Math.max(rightLargestYDataPoint, Math.abs(rightSmallestYDataPointMinus10Percent));
                const scale =
                    rightYScale ||
                    scaleLinear()
                        .domain([maxAbsValue, -maxAbsValue])
                        .range([margin, height - margin]);
                return shouldRoundYScales ? scale.nice() : scale;
            }
        } else {
            const scale =
                rightYScale ||
                scaleLinear()
                    .domain(rightYDomain)
                    .range([margin, height - margin]);

            return shouldRoundYScales ? scale.nice() : scale;
        }
    }, [
        height,
        margin,
        rightYDomain,
        rightYScale,
        shouldRoundYScales,
        graphType,
        rightLargestYDataPoint,
        rightSmallestYDataPointMinus10Percent,
    ]);

    let leftYDomain = useMemo(() => {
        if (shouldUseUnifiedScale && yScaleShouldStartAtZero) {
            return [unifiedMaxValue, 0];
        }
        return yScaleShouldStartAtZero
            ? [leftLargestYDataPoint, 0]
            : [leftLargestYDataPoint, leftSmallestYDataPointMinus10Percent];
    }, [
        leftLargestYDataPoint,
        leftSmallestYDataPointMinus10Percent,
        yScaleShouldStartAtZero,
        shouldUseUnifiedScale,
        unifiedMaxValue,
    ]);

    leftYScale = useMemo(() => {
        const leftZeroPosition =
            graphType === GraphType.TWO_UP_AND_ONE_DOWN_BARS && rightYScale
                ? rightYScale(0) ?? height - margin
                : height - margin;
        const scale = leftYScale || scaleLinear().domain(leftYDomain).range([margin, leftZeroPosition]);

        return shouldRoundYScales ? scale.nice() : scale;
    }, [height, margin, leftYScale, leftYDomain, shouldRoundYScales, graphType, rightYScale]);

    if (graphType === GraphType.TWO_UP_AND_ONE_DOWN_BARS) {
        yZeroPosition = rightYScale!(0);
    } else {
        yZeroPosition =
            yZeroPosition ?? graphType === GraphType.ONE_UP_AND_ONE_DOWN_BARS ? leftYScale(0) : rightYScale!(0);
    }

    useEffect(() => {
        setYZeroPosition(yZeroPosition);
    }, [leftYScale, rightYScale, setYZeroPosition, yZeroPosition]);

    const config = {
        data,
        xScale,
        leftYScale,
        rightYScale,
        formatDate,
        formatMessage,
        dimensions: {
            margin,
            horizontalMargin,
            height: height - margin,
            width: width - margin,
            widthWithoutMargin: width,
            heightWithoutMargin: height,
            legendHeight,
            legendLeftMargin,
            legendTopMargin,
        },
        hoverInfoXOffset,
        setHoverInfoXOffset,
        graphType,
        yZeroPosition: yZeroPosition ?? height - margin,
    };

    return <GraphContext.Provider value={config}>{children}</GraphContext.Provider>;
};

export default GraphDataProvider;
